import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, Clock, Snowflake, Zap, ChevronRight } from "lucide-react";

const METHOD_TONES = {
    GET: "bg-gray-100 text-gray-700",
    POST: "bg-blue-50 text-blue-700",
    PUT: "bg-violet-50 text-violet-700",
    PATCH: "bg-amber-50 text-amber-700",
    DELETE: "bg-red-50 text-red-700",
};

function formatTime(iso) {
    return new Date(iso).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });
}

function statusMeta(inv) {
    if (inv.was_throttled === "true" || inv.status === "THROTTLED")
        return { bar: "bg-amber-500", icon: Clock, tone: "text-amber-700" };
    if (inv.status === "FAILURE" || parseInt(inv.status_code, 10) >= 400)
        return { bar: "bg-red-500", icon: XCircle, tone: "text-red-700" };
    return { bar: "bg-green-500", icon: CheckCircle2, tone: "text-green-700" };
}

function Chip({ icon: Icon, label, cls }) {
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded border ${cls}`}>
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
}

function ActivityStream({ invocations, loading, onSelect }) {
    const { t } = useTranslation();

    if (loading && invocations.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex items-center gap-3 pl-5 pr-10 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
                    <span className="w-20">{t("activity.time") || "Time"}</span>
                    <span className="flex-1">{t("activity.endpoint") || "Endpoint"}</span>
                    <span className="w-28 text-right">{t("activity.latency") || "Latency"}</span>
                </div>
                <div className="divide-y divide-gray-100">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="relative flex items-center gap-3 pl-5 pr-10 py-3">
                            <span className="absolute left-0 top-0 bottom-0 w-1 bg-gray-100 animate-pulse" />
                            <div className="w-20 h-3 bg-gray-100 rounded animate-pulse" />
                            <div className="flex-1 flex items-center gap-2">
                                <div className="w-10 h-4 bg-gray-100 rounded animate-pulse" />
                                <div className="h-3 bg-gray-100 rounded animate-pulse" style={{ width: `${40 + (i * 7) % 40}%` }} />
                            </div>
                            <div className="w-20 h-3 bg-gray-100 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (invocations.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
                <p className="text-sm text-gray-500">{t("activity.noActivity")}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-3 pl-5 pr-10 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
                <span className="w-20">{t("activity.time") || "Time"}</span>
                <span className="flex-1">{t("activity.endpoint") || "Endpoint"}</span>
                <span className="w-28 text-right">{t("activity.latency") || "Latency"}</span>
            </div>
            <div className="divide-y divide-gray-100">
                {invocations.map((inv) => {
                    const meta = statusMeta(inv);
                    const StatusIcon = meta.icon;
                    const duration = parseInt(inv.duration, 10);
                    const method = (inv.method || "GET").toUpperCase();
                    const isSlow = duration > 2000;
                    const isColdStart = inv.is_cold_start === "true";
                    return (
                        <button
                            key={inv.id || inv.request_id}
                            onClick={() => onSelect(inv)}
                            className="group relative w-full flex items-center gap-3 pl-5 pr-10 py-3 text-left hover:bg-gray-50 transition-colors"
                        >
                            <span className={`absolute left-0 top-0 bottom-0 w-1 ${meta.bar}`} />
                            <span className="w-20 font-mono text-xs text-gray-400">
                                {formatTime(inv.created_at)}
                            </span>
                            <span className="flex-1 flex items-center gap-2 min-w-0">
                                <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${METHOD_TONES[method] || METHOD_TONES.GET}`}>
                                    {method}
                                </span>
                                <span className="font-mono text-sm text-gray-900 truncate">
                                    {inv.path}
                                </span>
                                {isColdStart && <Chip icon={Snowflake} label={t("activity.coldChip") || "cold"} cls="bg-blue-50 text-blue-700 border-blue-100" />}
                                {isSlow && <Chip icon={Zap} label={t("activity.slowChip") || "slow"} cls="bg-amber-50 text-amber-700 border-amber-100" />}
                            </span>
                            <span className={`flex items-center gap-1.5 w-28 justify-end ${meta.tone}`}>
                                <StatusIcon className="w-4 h-4 shrink-0" />
                                <span className={`font-mono text-sm tabular-nums ${isSlow ? "text-amber-700 font-medium" : "text-gray-700"}`}>
                                    {duration}ms
                                </span>
                            </span>
                            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 group-hover:text-gray-600 opacity-0 group-hover:opacity-100 transition" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default ActivityStream;
